import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getIntakeFlags,
  createIntakeFlag,
  updateIntakeFlag,
} from 'services/escriturasTemplatesService';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import TextArea from '../../components/TextArea/TextArea';
import { escriturasFlagsI18n } from './i18n';
import '../../styles/naEeppTableGrid.scss';

/** Lee el valor de un onChange que puede venir como evento (target.value) o valor crudo. */
const readVal = (e) =>
  e && typeof e === 'object' && 'target' in e ? e.target.value : e;

const FILTER_ALL = '__all__';
const SEVERITIES = ['Alta', 'Media', 'Baja'];
const SCOPES = ['HA', 'CONTRACT', 'BOTH'];
const INSTRUCTION_MAX = 4000;

const blankFlag = {
  id: null,
  title: '',
  instruction: '',
  severity: 'Media',
  scope: 'BOTH',
  isActive: true,
};

/**
 * Catálogo global de banderas jurídicas (IntakeFlags_SAAS). El agente de intake (A1)
 * vigila estas banderas en cada caso; crearlas/editarlas aquí basta para que se
 * evalúen sin tocar el prompt. El estado activo/inactivo se edita en el form (campo
 * Estado). Reusa el design system (Table/Modal/Button/IconButton/Badge/Input/Dropdown/
 * Skeleton) + TextArea local.
 */
export default function EscriturasFlags({ DesignSystemComponents }) {
  const { language } = useLanguage();
  const tplLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: escriturasFlagsI18n });

  const {
    Button,
    Table,
    Badge,
    IconButton,
    Dropdown,
    Modal,
    Input,
    Card,
    Skeleton,
  } = DesignSystemComponents ?? {};

  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null = lista; objeto = form (crear/editar)
  const [saving, setSaving] = useState(false);
  // Filtros + paginación client-side (el catálogo llega completo).
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(FILTER_ALL);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const submittingRef = useRef(false);
  const abortRef = useRef(null);
  // Solo para gatear setState post-await en mutaciones: no abortamos el request
  // (un guardado/desactivación debe completar en el server aunque el usuario navegue).
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    escriturasFlagsI18n.changeLanguage(tplLanguage);
  }, [tplLanguage]);

  const fetchFlags = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const data = await getIntakeFlags(true, controller.signal);
      setFlags(data || []);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(
        err.response?.data?.message || err.message || t('flag_error_generic')
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchFlags();
    return () => abortRef.current?.abort();
  }, [fetchFlags]);

  const canSubmit = (editing?.title || '').trim().length > 0;

  const handleSave = async () => {
    if (!canSubmit || submittingRef.current) return;
    submittingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        title: editing.title.trim(),
        instruction: (editing.instruction || '').trim() || null,
        severity: editing.severity,
        scope: editing.scope,
      };
      if (editing.id) {
        // Sin signal: la mutación debe completar en el server aunque el usuario navegue.
        await updateIntakeFlag(editing.id, {
          ...payload,
          isActive: editing.isActive,
        });
      } else {
        await createIntakeFlag(payload);
      }
      if (!mountedRef.current) return;
      setEditing(null);
      await fetchFlags();
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      if (!mountedRef.current) return;
      const conflict = err.response?.status === 409;
      setError(
        conflict
          ? t('flag_title_conflict')
          : err.response?.data?.message ||
              err.message ||
              t('flag_error_generic')
      );
    } finally {
      if (mountedRef.current) {
        setSaving(false);
      }
      submittingRef.current = false;
    }
  };

  // Misma paleta que el display al abogado en EscriturasIntakePanel:
  // Alta = rojo, Media = naranja, Baja = azul.
  const severityBadge = (r) => {
    const color =
      r.severity === 'Alta'
        ? 'error'
        : r.severity === 'Media'
          ? 'warning'
          : 'info';
    const label =
      r.severity === 'Alta'
        ? t('flag_severity_high')
        : r.severity === 'Media'
          ? t('flag_severity_medium')
          : t('flag_severity_low');
    return Badge ? (
      <Badge text={label} color={color} size='sm' />
    ) : (
      <span>{label}</span>
    );
  };

  const scopeLabel = (scope) => {
    if (scope === 'HA') return t('flag_scope_ha');
    if (scope === 'CONTRACT') return t('flag_scope_contract');
    return t('flag_scope_both');
  };

  const statusBadge = (r) => {
    const text = r.isActive ? t('flag_active') : t('flag_inactive');
    const color = r.isActive ? 'success' : 'light';
    return Badge ? (
      <Badge text={text} color={color} size='sm' />
    ) : (
      <span>{text}</span>
    );
  };

  const instructionExcerpt = (value) => {
    if (!value) return '—';
    const clean = value.trim();
    return clean.length > 90 ? `${clean.slice(0, 90)}…` : clean;
  };

  const columns = [
    { accessor: 'title', label: t('flag_col_title') },
    {
      accessor: 'severity',
      label: t('flag_col_severity'),
      render: severityBadge,
    },
    {
      accessor: 'scope',
      label: t('flag_col_scope'),
      render: (r) => scopeLabel(r.scope),
    },
    {
      accessor: 'instruction',
      label: t('flag_col_instruction'),
      render: (r) => (
        <span title={r.instruction || ''}>
          {instructionExcerpt(r.instruction)}
        </span>
      ),
    },
    { accessor: 'isActive', label: t('flag_col_status'), render: statusBadge },
    {
      label: t('flag_col_actions'),
      render: (r) => (
        <div className='d-flex gap-2 align-items-center'>
          {IconButton ? (
            <IconButton
              ariaLabel={t('flag_action_edit')}
              icon='pencil'
              size='sm'
              stroke='var(--primary-500)'
              onClick={() => setEditing({ ...r })}
            />
          ) : (
            <button
              type='button'
              className='btn btn-link btn-sm'
              onClick={() => setEditing({ ...r })}
            >
              {t('flag_action_edit')}
            </button>
          )}
        </div>
      ),
    },
  ];

  const statusOptions = useMemo(
    () => [
      { label: t('flag_filter_all_status'), value: FILTER_ALL },
      { label: t('flag_active'), value: 'active' },
      { label: t('flag_inactive'), value: 'inactive' },
    ],
    [t]
  );

  const severityOptions = useMemo(
    () =>
      SEVERITIES.map((s) => ({
        label:
          s === 'Alta'
            ? t('flag_severity_high')
            : s === 'Media'
              ? t('flag_severity_medium')
              : t('flag_severity_low'),
        value: s,
      })),
    [t]
  );

  const scopeOptions = useMemo(
    () => SCOPES.map((s) => ({ label: scopeLabel(s), value: s })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  const activeOptions = useMemo(
    () => [
      { label: t('flag_active'), value: true },
      { label: t('flag_inactive'), value: false },
    ],
    [t]
  );

  const visibleFlags = useMemo(() => {
    const q = search.trim().toLowerCase();
    return flags.filter((f) => {
      if (q && !`${f.title} ${f.instruction || ''}`.toLowerCase().includes(q))
        return false;
      if (statusFilter === 'active' && !f.isActive) return false;
      if (statusFilter === 'inactive' && f.isActive) return false;
      return true;
    });
  }, [flags, search, statusFilter]);

  const ContentWrapper = Card || 'div';
  const contentWrapperProps = Card
    ? { specialPadding: 'p-4' }
    : { className: 'container-fluid p-4' };

  const formBody = editing && (
    <div>
      {error && (
        <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>
          {error}
        </div>
      )}
      <div className='d-flex flex-column gap-3'>
        {Input && (
          <Input
            label={t('flag_field_title')}
            value={editing.title}
            onChange={(e) => setEditing((s) => ({ ...s, title: readVal(e) }))}
            disabled={saving}
            maxLength={256}
            width='100%'
          />
        )}
        <div className='d-flex flex-wrap gap-3'>
          {Dropdown && (
            <div style={{ flex: '1 1 200px' }}>
              <label className='caption font-light d-block mb-1'>
                {t('flag_field_severity')}
              </label>
              <Dropdown
                options={severityOptions}
                value={
                  severityOptions.find((o) => o.value === editing.severity) ||
                  severityOptions[0]
                }
                onChange={(o) =>
                  setEditing((s) => ({ ...s, severity: o?.value ?? 'Media' }))
                }
                width='100%'
              />
            </div>
          )}
          {Dropdown && (
            <div style={{ flex: '1 1 200px' }}>
              <label className='caption font-light d-block mb-1'>
                {t('flag_field_scope')}
              </label>
              <Dropdown
                options={scopeOptions}
                value={
                  scopeOptions.find((o) => o.value === editing.scope) ||
                  scopeOptions[scopeOptions.length - 1]
                }
                onChange={(o) =>
                  setEditing((s) => ({ ...s, scope: o?.value ?? 'BOTH' }))
                }
                width='100%'
              />
            </div>
          )}
          {Dropdown && editing.id && (
            <div style={{ flex: '1 1 200px' }}>
              <label className='caption font-light d-block mb-1'>
                {t('flag_field_active')}
              </label>
              <Dropdown
                options={activeOptions}
                value={
                  activeOptions.find((o) => o.value === editing.isActive) ||
                  activeOptions[0]
                }
                onChange={(o) =>
                  setEditing((s) => ({ ...s, isActive: o?.value ?? true }))
                }
                width='100%'
              />
            </div>
          )}
        </div>
        <TextArea
          label={t('flag_field_instruction')}
          helper={t('flag_field_instruction_helper')}
          value={editing.instruction}
          onChange={(e) =>
            setEditing((s) => ({ ...s, instruction: readVal(e) }))
          }
          disabled={saving}
          rows={4}
          maxLength={INSTRUCTION_MAX}
          showCount
        />
      </div>
      <div className='d-flex justify-content-end gap-2 mt-3'>
        {Button && (
          <Button
            label={t('flag_btn_cancel')}
            size='sm'
            variant='secondary'
            onClick={() => {
              setEditing(null);
              setError(null);
            }}
            disabled={saving}
          />
        )}
        {Button && (
          <Button
            label={saving ? t('flag_loading') : t('flag_btn_save')}
            size='sm'
            variant='primary'
            onClick={handleSave}
            disabled={!canSubmit || saving}
          />
        )}
      </div>
    </div>
  );

  return (
    <div>
      <div className='mb-4 d-flex justify-content-between align-items-start'>
        <div>
          <h2 className='fw-semibold'>{t('flag_title')}</h2>
          <h4 className='fw-normal'>{t('flag_subtitle')}</h4>
        </div>
        <div className='d-flex gap-2 align-items-center'>
          {Button && (
            <Button
              label={t('flag_new')}
              size='sm'
              variant='primary'
              onClick={() => {
                setError(null);
                setEditing({ ...blankFlag });
              }}
            />
          )}
          {IconButton && (
            <IconButton
              ariaLabel={t('flag_refresh')}
              icon='arrowCounterclockwise'
              size='lg'
              iconFill='var(--primary-500)'
              onClick={fetchFlags}
              disabled={loading}
            />
          )}
        </div>
      </div>

      <ContentWrapper {...contentWrapperProps}>
        {error && !editing && (
          <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* Filtros: búsqueda (título/instrucción) + estado */}
        <div className='d-flex flex-wrap gap-2 mb-3' style={{ maxWidth: 720 }}>
          {Input && (
            <div style={{ flex: '1 1 240px' }}>
              <Input
                icon='search'
                placeholder={t('flag_search_placeholder')}
                value={search}
                onChange={(e) => {
                  setSearch(readVal(e));
                  setPage(0);
                }}
                width='100%'
              />
            </div>
          )}
          {Dropdown && (
            <div style={{ flex: '0 1 200px' }}>
              <Dropdown
                options={statusOptions}
                value={
                  statusOptions.find((o) => o.value === statusFilter) ||
                  statusOptions[0]
                }
                onChange={(o) => {
                  setStatusFilter(o?.value ?? FILTER_ALL);
                  setPage(0);
                }}
                width='100%'
              />
            </div>
          )}
        </div>

        <div className='table-container na-eepp-grid-6'>
          {loading && Skeleton ? (
            <Skeleton count={6} height={40} />
          ) : Table ? (
            <Table
              columns={columns}
              data={visibleFlags}
              loading={loading}
              paginator
              paginationMode='client'
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setPage(0);
              }}
              totalItems={visibleFlags.length}
              paginatorGroup={[
                { label: '10', value: 10 },
                { label: '20', value: 20 },
                { label: '50', value: 50 },
              ]}
            />
          ) : (
            <p className='font-light'>{loading ? t('flag_loading') : null}</p>
          )}
          {!loading && visibleFlags.length === 0 && (
            <div className='text-center p-4'>
              <p className='caption font-light'>
                {search.trim() || statusFilter !== FILTER_ALL
                  ? t('flag_search_empty')
                  : t('flag_empty')}
              </p>
            </div>
          )}
        </div>

        {/* Modal crear/editar */}
        {editing &&
          (Modal ? (
            <Modal
              title={editing.id ? t('flag_edit_title') : t('flag_create_title')}
              show
              onClose={() => {
                if (saving) return;
                setEditing(null);
                setError(null);
              }}
              primaryBtnTxt={null}
              secondaryBtnTxt={null}
              width='640px'
            >
              {formBody}
            </Modal>
          ) : (
            <div
              role='dialog'
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(15,23,42,0.55)',
                zIndex: 9500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 32,
              }}
              onClick={() => {
                if (saving) return;
                setEditing(null);
                setError(null);
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: '#fff',
                  borderRadius: 8,
                  padding: 20,
                  width: 'min(640px, 94vw)',
                }}
              >
                <h5>
                  {editing.id ? t('flag_edit_title') : t('flag_create_title')}
                </h5>
                {formBody}
              </div>
            </div>
          ))}
      </ContentWrapper>
    </div>
  );
}
